<template>
  <form @submit.prevent="submitForm">
    <!-- Title -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Название курса *</label>
      <input
        v-model="form.title"
        type="text"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите название курса"
      />
    </div>

    <!-- Description -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Описание *</label>
      <textarea
        v-model="form.description"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите описание курса"
        rows="5"
      ></textarea>
    </div>

    <!-- Image -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Изображение</label>
      <input
        type="file"
        @change="handleImageUpload"
        accept="image/*"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <div v-if="imagePreview" class="mt-3">
        <img :src="imagePreview" alt="Preview" class="w-32 h-32 object-cover rounded" />
      </div>
    </div>

    <!-- Course Type -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Тип проведения *</label>
      <select
        v-model="form.course_type"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="">Выберите тип</option>
        <option value="online">Онлайн</option>
        <option value="offline">Офлайн</option>
      </select>
    </div>

    <!-- Max Amount -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Максимальное количество мест *</label>
      <input
        v-model.number="form.max_amount"
        type="number"
        required
        min="1"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите количество мест"
      />
    </div>

    <!-- Hours -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Количество часов</label>
      <input
        v-model.number="form.hours"
        type="number"
        min="0"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите количество часов"
      />
    </div>

    <!-- Price -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Цена</label>
      <input
        v-model.number="form.price"
        type="number"
        step="0.01"
        min="0"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите цену"
      />
    </div>

    <!-- Content -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Контент курса</label>
      <p class="text-gray-600 text-xs mb-3">Добавьте подробное описание программы, модулей и целей обучения</p>
      <QuillEditor
        :content="form.content"
        @update:content="(val) => form.content = val"
        content-type="html"
        theme="snow"
        class="bg-white"
      />
    </div>

    <!-- Info Message -->
    <div class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <p class="text-blue-800 text-sm">
        ✓ Ваше предложение курса будет рассмотрено модератором в течение 3 рабочих дней.
        После одобрения курс появится на платформе.
      </p>
    </div>

    <!-- Buttons -->
    <div class="flex gap-4">
      <button
        type="submit"
        :disabled="loading"
        class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-bold text-sm uppercase"
      >
        <span v-if="loading">Отправка...</span>
        <span v-else>Предложить курс</span>
      </button>
      <button
        type="button"
        @click="$emit('close')"
        class="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-bold text-sm uppercase"
      >
        Отмена
      </button>
    </div>
  </form>
</template>

<script>
import { ref } from 'vue';
import axios from 'axios';
import { HEADERS_TOKEN_JSON, API_COURSE_SUGGEST } from '@/const/uri';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'CourseSuggestForm',
  components: {
    QuillEditor,
  },
  emits: ['submit', 'close'],
  setup(props, { emit }) {
    const form = ref({
      title: '',
      description: '',
      course_type: '',
      max_amount: 1,
      hours: 0,
      price: 0,
      content: '',
    });

    const loading = ref(false);
    const imageFile = ref(null);
    const imagePreview = ref(null);

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        imageFile.value = file;
        imagePreview.value = URL.createObjectURL(file);
      }
    };

    const submitForm = async () => {
      if (!form.value.title || !form.value.description || !form.value.course_type || !form.value.max_amount) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      loading.value = true;
      try {
        const formData = new FormData();
        formData.append('title', form.value.title);
        formData.append('description', form.value.description);
        formData.append('course_type', form.value.course_type);
        formData.append('max_amount', form.value.max_amount);
        formData.append('hours', form.value.hours || 0);
        formData.append('price', form.value.price || 0);
        formData.append('content', form.value.content || '');

        if (imageFile.value) {
          formData.append('image', imageFile.value);
        }

        const response = await axios.post(
          API_COURSE_SUGGEST.create,
          formData,
          {
            headers: {
              ...HEADERS_TOKEN_JSON(),
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data.code === 0 || response.status === 201) {
          emit('submit', response.data.data);
          alert('Спасибо! Ваше предложение курса отправлено на модерацию.');
        }
      } catch (error) {
        console.error('Ошибка при отправке формы:', error);
        alert('Ошибка при отправке формы. Пожалуйста, попробуйте снова.');
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      loading,
      imagePreview,
      handleImageUpload,
      submitForm,
    };
  },
};
</script>

<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
